import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import type { PrivateGuidebookPayload, PublicGuidebookPayload } from "./types"
import {
  fetchGuestGuidebookBySlug,
  fetchPublicGuidebookBySlug,
  getGuestSessionStorageKey,
} from "./api"
import { GuidebookShell } from "./GuidebookShell"
import { ConciergeShell } from "./concierge/ConciergeShell"
import { UnlockStayModal } from "./UnlockStayModal"
import { ReserveShell } from "./reserve/ReserveShell"
import { ReserveUnlockDialog } from "./reserve/ReserveUnlockDialog"
import "./guidebook.css"

const CONCIERGE_SUB_MESSAGES = [
  "Gathering local favourites\u2026",
  "Reviewing what\u2019s nearby\u2026",
  "Selecting places worth your time\u2026",
  "Considering today\u2019s plans\u2026",
  "Finalising your guide\u2026",
]

/** Default grace period before showing loading hero (ms). If data arrives sooner, hero is skipped. */
const DEFAULT_HERO_GRACE_MS = 300

function GuidebookLoadingHero() {
  const [index, setIndex] = useState(0)
  const [fading, setFading] = useState(false)
  const isFirstRotation = useRef(true)

  useEffect(() => {
    if (CONCIERGE_SUB_MESSAGES.length <= 1) return

    const delay = isFirstRotation.current ? 1200 : 2400

    const timer = setTimeout(() => {
      setFading(true)
      setTimeout(() => {
        setIndex(i => (i + 1) % CONCIERGE_SUB_MESSAGES.length)
        setFading(false)
        isFirstRotation.current = false
      }, 200)
    }, delay)

    return () => clearTimeout(timer)
  }, [index])

  return (
    <div className="guidebook">
      <div className="gb-loading-hero">
        <span className="gb-loading-hero__badge">
          <span className="gb-loading-hero__dot" />
          Stayframe
        </span>
        <span className="gb-loading-hero__anchor">
          Preparing your personalised guide&#8230;
        </span>
        <span
          className="gb-loading-hero__message"
          style={{ opacity: fading ? 0 : 1 }}
        >
          {CONCIERGE_SUB_MESSAGES[index]}
        </span>
      </div>
      <div className="gb-skeleton gb-skeleton--card" />
      <div className="gb-skeleton gb-skeleton--card" />
    </div>
  )
}

/**
 * Guidebook loading gate: skip the hero for fast/cached loads, show it only
 * while a genuine load is in flight.
 *
 * State machine: "deciding" → "hero" (if still loading after the grace
 * period) or "done" (if data arrives fast). The hero is dismissed the moment
 * the load completes — no artificial hold.
 */
function useGuidebookLoadingGate(
  isLoading: boolean,
  hasData: boolean,
  gateConfig?: { guidebook_grace_ms: number } | null,
) {
  const graceMs = gateConfig?.guidebook_grace_ms ?? DEFAULT_HERO_GRACE_MS

  type HeroState = "deciding" | "hero" | "done"
  const [state, setState] = useState<HeroState>("deciding")

  // Grace period: transition from "deciding" → "hero" if still loading after grace
  useEffect(() => {
    if (state !== "deciding") return
    if (!isLoading || hasData) {
      // Data arrived during grace period — skip hero entirely
      setState("done")
      return
    }
    const timer = setTimeout(() => setState("hero"), graceMs)
    return () => clearTimeout(timer)
  }, [state, isLoading, hasData, graceMs])

  // Dismiss the hero as soon as the genuine load completes — no artificial
  // minimum hold.
  useEffect(() => {
    if (state === "hero" && !isLoading) setState("done")
  }, [state, isLoading])

  return { showHero: state === "hero", deciding: state === "deciding" }
}

export function GuestGuidebookPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const [publicData, setPublicData] = useState<PublicGuidebookPayload | null>(null)
  const [privateData, setPrivateData] = useState<PrivateGuidebookPayload | null>(null)
  const [publicError, setPublicError] = useState<string | null>(null)
  const [loadingPublic, setLoadingPublic] = useState(true)
  const [checkingPrivate, setCheckingPrivate] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const [unlockOpen, setUnlockOpen] = useState(false)
  const [guestToken, setGuestToken] = useState("")

  const normalizedSlug = useMemo(() => String(slug || "").trim().toLowerCase(), [slug])
  const storageKey = useMemo(() => getGuestSessionStorageKey(normalizedSlug), [normalizedSlug])

  const loadPrivateGuidebook = useCallback(async (token: string) => {
    const safeToken = String(token || "").trim()
    if (!safeToken || !normalizedSlug) {
      setPrivateData(null)
      return
    }

    setCheckingPrivate(true)
    try {
      const privatePayload = await fetchGuestGuidebookBySlug(normalizedSlug, safeToken)
      if (!privatePayload.ok) {
        throw new Error(privatePayload.error || "Failed to load guidebook.")
      }
      setPrivateData(privatePayload)
      if (privatePayload.property?.name) {
        document.title = `${privatePayload.property.name} — Guidebook`
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not load guidebook."
      if (message.toLowerCase().includes("verification required") || message.toLowerCase().includes("expired")) {
        sessionStorage.removeItem(storageKey)
        setGuestToken("")
      }
      setPrivateData(null)
    } finally {
      setCheckingPrivate(false)
    }
  }, [normalizedSlug, storageKey])

  useEffect(() => {
    if (!normalizedSlug) {
      setPublicError("Invalid property link.")
      setLoadingPublic(false)
      setPublicData(null)
      setPrivateData(null)
      return
    }

    let cancelled = false
    setLoadingPublic(true)
    setPublicError(null)

    fetchPublicGuidebookBySlug(normalizedSlug)
      .then((result) => {
        if (cancelled) return
        if (!result.ok) {
          setPublicError(result.error || "Failed to load guidebook.")
          return
        }
        setPublicData(result)
        if (result.property?.name) {
          document.title = `${result.property.name} — Guidebook`
        }
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : "Could not load guidebook."
        setPublicError(message)
      })
      .finally(() => {
        if (!cancelled) setLoadingPublic(false)
      })

    const token = sessionStorage.getItem(storageKey) || ""
    setGuestToken(token)
    if (!token) {
      setPrivateData(null)
      setCheckingPrivate(false)
    } else {
      void loadPrivateGuidebook(token)
    }

    return () => { cancelled = true }
  }, [normalizedSlug, storageKey, refreshKey, loadPrivateGuidebook])

  const data = privateData || publicData
  const { showHero, deciding } = useGuidebookLoadingGate(loadingPublic, !!data, publicData?.intelligence_gate)

  // During grace period (< 300ms): show nothing (imperceptible)
  if (deciding && !data) return null

  // Loading hero: genuine load, enforce minimum duration
  if (showHero) return <GuidebookLoadingHero />

  if (!data) {
    return (
      <div className="guidebook">
        <div className="guidebook__error">
          <h1 className="guidebook__error-title">Guidebook unavailable</h1>
          <p className="guidebook__error-message">{publicError || "Could not load guidebook."}</p>
          <button
            className="guidebook__error-retry"
            onClick={() => setRefreshKey((current) => current + 1)}
            type="button"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const authMode = publicData?.authMode || data.authMode || "pms"
  const requiresPinForSensitive = Boolean(publicData?.requiresPinForSensitive || data.requiresPinForSensitive)
  const shouldShowUnlockPrompt = !privateData
    && !checkingPrivate
    && Array.isArray(publicData?.sensitiveFields)
    && publicData.sensitiveFields.length > 0

  const template = data.template ?? "classic"

  const sharedProps = {
    data,
    slug: normalizedSlug,
    guestToken,
    onRefresh: () => {
      if (guestToken) {
        void loadPrivateGuidebook(guestToken)
        return
      }
      setRefreshKey((current) => current + 1)
    },
    showUnlockPrompt: shouldShowUnlockPrompt,
    onOpenUnlock: () => setUnlockOpen(true),
  }

  const unlockModal = template === "reserve"
    ? (
        <ReserveUnlockDialog
          open={unlockOpen}
          slug={normalizedSlug}
          authMode={authMode}
          requiresPinForSensitive={requiresPinForSensitive}
          onClose={() => setUnlockOpen(false)}
          onSuccess={async (token) => {
            sessionStorage.setItem(storageKey, token)
            setGuestToken(token)
            await loadPrivateGuidebook(token)
          }}
        />
      )
    : (
        <UnlockStayModal
          open={unlockOpen}
          slug={normalizedSlug}
          authMode={authMode}
          requiresPinForSensitive={requiresPinForSensitive}
          onClose={() => setUnlockOpen(false)}
          onSuccess={async (token) => {
            sessionStorage.setItem(storageKey, token)
            setGuestToken(token)
            await loadPrivateGuidebook(token)
          }}
        />
      )

  if (template === "concierge") {
    return (
      <>
        <ConciergeShell {...sharedProps} />
        {unlockModal}
      </>
    )
  }

  if (template === "reserve") {
    return (
      <>
        <ReserveShell {...sharedProps} />
        {unlockModal}
      </>
    )
  }

  return (
    <div className="guidebook">
      <GuidebookShell {...sharedProps} />
      {unlockModal}
    </div>
  )
}
